import { useRef, useState } from 'react';
import { AuthImage } from '../../components/AuthImage';
import { IconLink } from '../../components/icons';
import { Badge, Card, EmptyState, Loading, Modal, ModuleHeader, useAsync, useToast } from '../../components/ui';
import { api, downloadFile, request } from '../../lib/api';
import { formatDateTime } from '../../lib/format';
import { useAuth } from '../../lib/auth';

const kilobyte = (bytes: number) =>
  bytes >= 1024 * 1024 ? `${(bytes / 1024 / 1024).toFixed(1)} MB` : `${Math.round(bytes / 1024)} KB`;

/**
 * QR-Codes am Objekt (Kap. 5) – Aushang für Meldungen von Dritten ohne
 * Benutzerkonto. Ein Aushang ist kein Geheimnis; „erneuern“ entwertet einen
 * abfotografierten oder verlorenen Code, „nachtragen“ ist dieselbe Aktion für
 * ein Objekt, das noch nie einen hatte.
 */
export function QrSettings() {
  const { notify } = useToast();
  const { can } = useAuth();
  const properties = useAsync(() => api.get('/api/properties', { includeInactive: 'true' }), []);
  const design = useAsync(() => api.get('/api/documents/design'), []);
  const [preview, setPreview] = useState<any | null>(null);
  const [busyId, setBusyId] = useState<number | null>(null);
  const [busyBackground, setBusyBackground] = useState(false);
  const backgroundInput = useRef<HTMLInputElement>(null);

  const uploadBackground = async (datei: File) => {
    setBusyBackground(true);
    try {
      const dataUrl = await new Promise<string>((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(String(reader.result));
        reader.onerror = () => reject(new Error('Die Datei konnte nicht gelesen werden.'));
        reader.readAsDataURL(datei);
      });
      const antwort: any = await api.put('/api/documents/assets/qr_background', { filename: datei.name, dataUrl });
      notify(`Hintergrund hinterlegt: ${antwort.asset?.note ?? ''}`, 'success');
      design.reload();
    } catch (error) {
      notify(error instanceof Error ? error.message : 'Hochladen nicht möglich.', 'error');
    } finally {
      setBusyBackground(false);
      if (backgroundInput.current) backgroundInput.current.value = '';
    }
  };

  const removeBackground = async () => {
    if (!window.confirm('Eigenen Hintergrund für das QR-Druckblatt entfernen?')) return;
    setBusyBackground(true);
    try {
      await api.delete('/api/documents/assets/qr_background');
      notify('Entfernt.', 'success');
      design.reload();
    } catch (error) {
      notify(error instanceof Error ? error.message : 'Entfernen nicht möglich.', 'error');
    } finally {
      setBusyBackground(false);
    }
  };

  const rotate = async (property: any) => {
    const hasToken = !!property.qr_token;
    const question = hasToken
      ? `Token für „${property.name}“ erneuern? Der bisherige Aushang zeigt danach auf eine ungültige Adresse und muss ausgetauscht werden.`
      : `QR-Code für „${property.name}“ anlegen?`;
    if (!window.confirm(question)) return;

    setBusyId(property.id);
    try {
      await api.post(`/api/properties/${property.id}/qr-token/rotate`);
      notify(hasToken ? 'Token erneuert.' : 'QR-Code angelegt.', 'success');
      properties.reload();
    } catch (error) {
      notify(error instanceof Error ? error.message : 'Aktion nicht möglich.', 'error');
    } finally {
      setBusyId(null);
    }
  };

  const remove = async (property: any) => {
    if (
      !window.confirm(
        `QR-Code für „${property.name}“ löschen? Der bisherige Aushang zeigt danach ins Leere, ohne Ersatzcode.`,
      )
    ) {
      return;
    }
    setBusyId(property.id);
    try {
      await api.delete(`/api/properties/${property.id}/qr-token`);
      notify('QR-Code gelöscht.', 'success');
      properties.reload();
    } catch (error) {
      notify(error instanceof Error ? error.message : 'Aktion nicht möglich.', 'error');
    } finally {
      setBusyId(null);
    }
  };

  if (properties.loading) return <Loading />;

  const rows = properties.data ?? [];

  return (
    <div className="stack">
      <ModuleHeader
        icon={<IconLink width={22} height={22} />}
        title="QR-Codes am Objekt"
        subtitle="Jeder Aushang führt zur öffentlichen Meldeseite des Objekts – ohne Anmeldung. Ein Aufkleber ist kein Geheimnis; wer ihn verloren glaubt oder ersetzen will, erneuert den Token."
        pill="Konfiguration"
        tone="info"
      />

      {can('admin.settings') && (
        <Card
          title="Hintergrund für das Druckblatt"
          subtitle="Eigenes Poster-Layout für das QR-Druckblatt – unabhängig vom allgemeinen Briefbogen"
          actions={
            design.data?.assets?.qr_background && (
              <button className="btn ghost sm" disabled={busyBackground} onClick={removeBackground}>
                Entfernen
              </button>
            )
          }
        >
          <div className="stack" style={{ gap: 12 }}>
            {design.data?.assets?.qr_background ? (
              <div className="note-box tone-success">
                <div className="row" style={{ justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
                  <div>
                    <div style={{ fontWeight: 600 }}>{design.data.assets.qr_background.filename}</div>
                    <div className="small subtle">
                      {design.data.assets.qr_background.note} · {kilobyte(design.data.assets.qr_background.size_bytes)} · geändert{' '}
                      {formatDateTime(design.data.assets.qr_background.updated_at)}
                    </div>
                  </div>
                  <a
                    className="btn sm ghost"
                    href="#"
                    onClick={async (event) => {
                      event.preventDefault();
                      const antwort = await request<Response>('/api/documents/assets/qr_background/file', { raw: true });
                      window.open(URL.createObjectURL(await antwort.blob()), '_blank');
                    }}
                  >
                    Ansehen
                  </a>
                </div>
              </div>
            ) : (
              <div className="note-box tone-info">
                Kein eigener Hintergrund hinterlegt – das Druckblatt nutzt den allgemeinen Briefbogen, falls einer
                aktiv ist, sonst den eingebauten Kopf.
              </div>
            )}
            <div className="row" style={{ gap: 10, flexWrap: 'wrap' }}>
              <input
                ref={backgroundInput}
                type="file"
                accept="application/pdf,.pdf"
                style={{ display: 'none' }}
                onChange={(event) => {
                  const datei = event.target.files?.[0];
                  if (datei) void uploadBackground(datei);
                }}
              />
              <button className="btn" disabled={busyBackground} onClick={() => backgroundInput.current?.click()}>
                {busyBackground ? 'Wird geprüft …' : design.data?.assets?.qr_background ? 'Andere PDF wählen' : 'PDF wählen …'}
              </button>
            </div>
          </div>
        </Card>
      )}

      <Card noBody>
        {rows.length === 0 ? (
          <EmptyState title="Keine Objekte angelegt" />
        ) : (
          <div className="table-wrap">
            <table className="data">
              <thead>
                <tr>
                  <th>Objekt</th>
                  <th>Anschrift</th>
                  <th>QR-Code</th>
                  <th className="actions">Aktionen</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((property: any) => (
                  <tr key={property.id} style={property.is_active ? undefined : { opacity: 0.55 }}>
                    <td>
                      <strong>{property.name}</strong>
                    </td>
                    <td className="small subtle">
                      {[property.street, [property.zip, property.city].filter(Boolean).join(' ')]
                        .filter(Boolean)
                        .join(', ') || '–'}
                    </td>
                    <td>
                      {property.qr_token ? (
                        <Badge variant="success">vorhanden</Badge>
                      ) : (
                        <Badge variant="warning">fehlt</Badge>
                      )}
                    </td>
                    <td className="actions">
                      <div className="row" style={{ gap: 6, justifyContent: 'flex-end' }}>
                        {property.qr_token && (
                          <button className="btn sm" onClick={() => setPreview(property)}>
                            Ansehen
                          </button>
                        )}
                        <button className="btn sm" disabled={busyId === property.id} onClick={() => rotate(property)}>
                          {property.qr_token ? 'Token erneuern' : 'QR-Code anlegen'}
                        </button>
                        {property.qr_token && (
                          <button className="btn sm ghost" disabled={busyId === property.id} onClick={() => remove(property)}>
                            Löschen
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {preview && (
        <Modal title={`QR-Code – ${preview.name}`} onClose={() => setPreview(null)}>
          <div className="stack" style={{ alignItems: 'center', gap: 14 }}>
            <div style={{ width: 220, height: 220, background: '#fff', padding: 12, borderRadius: 8 }}>
              <AuthImage
                path={`/api/properties/${preview.id}/qr.svg`}
                alt={`QR-Code ${preview.name}`}
                style={{ width: '100%', height: '100%' }}
              />
            </div>
            <div className="row" style={{ gap: 8 }}>
              <button
                className="btn"
                onClick={() =>
                  downloadFile(`/api/properties/${preview.id}/qr.svg`, `qr-code-${preview.id}.svg`)
                }
              >
                SVG herunterladen
              </button>
              <button
                className="btn primary"
                onClick={() =>
                  downloadFile(`/api/properties/${preview.id}/qr.pdf`, `qr-aushang-${preview.id}.pdf`)
                }
              >
                Druckblatt (PDF)
              </button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}
